import { map } from 'lodash';
import { z } from 'zod';

import {
  type Document,
  DocumentSchema,
  MissingDocumentSchema,
} from '@/modules/documents/domain/entities/document';
import type { IDocumentRepository } from '@/modules/documents/domain/repositories/document.repository';

import { type BodyOf, FeApi, multipartBoolean } from '../fetch-client';

import {
  documentMapper,
  missingDocumentMapper,
} from './entities/document.mapper';

export namespace DocumentRepositoryMapper {
  export namespace create {
    export function bodyFromInput(
      input: IDocumentRepository.CreateInput,
    ): BodyOf<typeof FeApi.recordsV1DocumentsUploadCreate> {
      return {
        documentUploadViewInputRequest: {
          file: input.document.file,
          name: input.document.name,
          tags: input.document.tags,
          type: input.document.type,
          sites_accure_ids: input.siteIds,
          is_global: multipartBoolean(input.document.isGlobal),
        },
      };
    }
  }

  export namespace deleteById {
    export function bodyFromInput(
      input: IDocumentRepository.DeleteInput,
    ): BodyOf<typeof FeApi.recordsV1DocumentsDeleteDestroy> {
      return {
        uuid: input.document.id,
      };
    }
  }

  export namespace getAll {
    export const outputSchema = z.object({
      documents: z.array(DocumentSchema),
      missingDocuments: z.array(MissingDocumentSchema),
    });

    export function outputFromData(
      data: FeApi.DocumentListViewOutputRead,
    ): Promise<IDocumentRepository.GetAllOutput> {
      return outputSchema.parseAsync({
        documents: map(data.documents, documentMapper),
        missingDocuments: map(data.missing, missingDocumentMapper),
      });
    }

    export function bodyFromInput(
      input: IDocumentRepository.GetAllInput,
    ): BodyOf<typeof FeApi.recordsV1DocumentsListRetrieve> {
      return {
        sitesAccureIds: input.siteIds,
        // INFO: if not site ids are provided, we want to get global documents
        isGlobal: input.siteIds.length === 0,
      };
    }
  }

  export namespace download {
    export function bodyFromInput(
      input: IDocumentRepository.DownloadInput,
    ): BodyOf<typeof FeApi.recordsV1DocumentsDownloadRetrieve> {
      return {
        uuid: input.document.id,
      };
    }

    export function outputFromData(file: Blob, document: Document): File {
      return new File([file], document.file.name);
    }
  }

  export namespace update {
    export function bodyFromInput(
      input: IDocumentRepository.UpdateInput,
    ): BodyOf<typeof FeApi.recordsV1DocumentsUpdateUpdate> {
      return {
        uuid: input.document.id,
        documentUpdateViewInputRequest: {
          new_file_name: input.document.name,
          new_tags: input.document.tags,
          new_type: input.document.type,
          new_sites_accure_ids: input.document.siteIds,
        },
      };
    }
  }
}
